import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { collection, doc, getDocs, getFirestore, query, setDoc, updateDoc, where } from 'firebase/firestore';
import SummitsPanel from './SummitsPanel';
import EducationTable from './EducationTable';
import Skills from './Skills';
import AthleticHighlights from './AthleticHighlights';
import ClimberScore from './ClimberScore';
import defaultProfileImage from '../assets/default-profile.png';
import './UserProfile.css';

const Icon = ({ name }) => {
  const paths = {
    pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2"/></>,
    shield: <><path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6Z"/><path d="m9 12 2 2 4-5"/></>,
    mountain: <><path d="m3 19 6-11 3 5 2-3 7 9Z"/><path d="m7 12 2 2 2-2"/></>,
    book: <><path d="M4 5a3 3 0 0 1 3-2h5v17H7a3 3 0 0 0-3 2ZM20 5a3 3 0 0 0-3-2h-5v17h5a3 3 0 0 1 3 2Z"/></>,
    spark: <><path d="m12 3 1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5Z"/><path d="m18 16 .7 2.3L21 19l-2.3.7L18 22l-.7-2.3L15 19l2.3-.7Z"/></>,
    edit: <><path d="m4 20 4-1 11-11-3-3L5 16Z"/><path d="m14 7 3 3"/></>,
    connect: <><circle cx="9" cy="8" r="3"/><path d="M3 19c0-4 2-6 6-6 2 0 3.5.5 4.5 1.5M18 12v6M15 15h6"/></>,
    flag: <><path d="M5 21V4M5 5h11l-2 4 2 4H5"/></>,
  };
  return <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};

function UserProfile() {
  const { userId } = useParams();
  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();
  const [isOwner, setIsOwner] = useState(false);
  const [userData, setUserData] = useState({ uid: '', userId: '', name: '', location: '', goals: '', imageUrl: '', email: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const usersRef = collection(db, 'users');
        const numericUserId = Number(userId);
        let snapshot = await getDocs(query(usersRef, where('userId', '==', Number.isNaN(numericUserId) ? userId : numericUserId)));
        if (snapshot.empty && !Number.isNaN(numericUserId)) snapshot = await getDocs(query(usersRef, where('userId', '==', userId)));
        if (snapshot.empty) {
          setError('No climber was found for this profile.');
          return;
        }
        const profile = snapshot.docs[0].data();
        setUserData(profile);
        setIsOwner(profile.uid === auth.currentUser?.uid);
      } catch (fetchError) {
        console.error(fetchError);
        setError('This climbing résumé could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [auth, db, userId]);

  const handleSave = async () => {
    if (!auth.currentUser || auth.currentUser.uid !== userData.uid) {
      setError('You must own this profile to edit it.');
      return;
    }
    try {
      const userDocRef = doc(db, 'users', userData.uid);
      let imageUrl = userData.imageUrl;
      if (imageFile) {
        const uploadResult = await uploadBytes(storageRef(storage, `profile_images/${userData.uid}`), imageFile);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }
      await setDoc(userDocRef, { name: userData.name, location: userData.location, goals: userData.goals, email: userData.email, imageUrl }, { merge: true });
      if (imageUrl !== userData.imageUrl) await updateDoc(userDocRef, { imageUrl });
      setUserData((profile) => ({ ...profile, imageUrl }));
      setImageFile(null);
      setEditMode(false);
      setError('');
    } catch (saveError) {
      console.error(saveError);
      setError(`Unable to update this profile: ${saveError.message}`);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setUserData((profile) => ({ ...profile, imageUrl: URL.createObjectURL(file) }));
  };
  const handleChange = (event) => setUserData((profile) => ({ ...profile, [event.target.name]: event.target.value }));

  if (loading) return <div className="profile-state">Loading climbing résumé…</div>;
  if (error && !userData.uid) return <div className="profile-state error-state">{error}<Link to="/profiles">Return to the directory</Link></div>;

  const role = userData.certification || userData.role || 'ClimberLink member';

  return (
    <div className="resume-page">
      <header className="resume-hero">
        <div className="resume-hero-overlay" />
        <div className="resume-identity">
          <img src={userData.imageUrl || defaultProfileImage} alt={userData.name || 'Climber'} />
          <div>
            <span className="resume-status"><Icon name="shield" /> {userData.certification ? 'VERIFIED CREDENTIAL' : 'ACTIVE PROFILE'}</span>
            <h1>{userData.name || 'Unnamed climber'}</h1>
            <p><Icon name="pin" /> {userData.location || 'Location not specified'} <i>•</i> {role}</p>
          </div>
        </div>
        <div className="resume-hero-actions">
          {isOwner ? <button onClick={() => setEditMode((editing) => !editing)}><Icon name="edit" /> {editMode ? 'Cancel editing' : 'Edit profile'}</button> : <><button><Icon name="connect" /> Connect</button><Link to="/Trips"><Icon name="flag" /> Invite to expedition</Link></>}
        </div>
      </header>

      {error && <div className="profile-alert">{error}</div>}

      {editMode && (
        <section className="profile-editor panel">
          <div><span className="eyebrow">PROFILE SETTINGS</span><h2>Update your climbing identity</h2></div>
          <div className="profile-editor-grid">
            <label><span>Profile photo</span><input type="file" accept="image/*" onChange={handleImageChange} /></label>
            <label><span>Name</span><input type="text" value={userData.name || ''} name="name" onChange={handleChange} /></label>
            <label><span>Home base</span><input type="text" value={userData.location || ''} name="location" onChange={handleChange} /></label>
            <label className="profile-goals-input"><span>Goals and objectives</span><textarea value={userData.goals || ''} name="goals" onChange={handleChange} /></label>
          </div>
          <div className="profile-editor-actions"><button onClick={() => setEditMode(false)}>Cancel</button><button onClick={handleSave}>Save profile</button></div>
        </section>
      )}

      <section className="resume-summary">
        <div><span>HOME RANGE</span><strong>{userData.location || 'Not specified'}</strong></div>
        <div><span>PRIMARY ROLE</span><strong>{role}</strong></div>
        <div><span>NEXT OBJECTIVE</span><strong>{userData.goals || 'Open to new objectives'}</strong></div>
      </section>

      <div className="resume-grid">
        <main className="resume-main">
          <section className="resume-panel">
            <div className="resume-panel-heading"><Icon name="mountain" /><div><span className="eyebrow">EXPEDITION HISTORY</span><h2>Summit experience</h2></div></div>
            <SummitsPanel userId={userData.uid} />
          </section>
          <section className="resume-panel">
            <div className="resume-panel-heading"><Icon name="spark" /><div><span className="eyebrow">PERFORMANCE</span><h2>Athletic highlights</h2></div></div>
            <AthleticHighlights userId={userData.uid} />
          </section>
          <section className="resume-panel">
            <div className="resume-panel-heading"><Icon name="book" /><div><span className="eyebrow">TRAINING & CERTIFICATION</span><h2>Education</h2></div></div>
            <EducationTable userId={userData.uid} />
          </section>
        </main>

        <aside className="resume-rail">
          <section className="score-card">
            <span className="eyebrow">CLIMBER SCORE</span>
            <div className="score-value"><ClimberScore userId={userData.uid} /><small>/ 100</small></div>
            <p>Calculated from recorded summits and cumulative elevation.</p>
          </section>
          <section className="resume-panel skills-panel">
            <div className="resume-panel-heading"><Icon name="shield" /><div><span className="eyebrow">CAPABILITIES</span><h2>Technical skills</h2></div></div>
            <Skills userId={userData.uid} />
          </section>
          <section className="objective-card">
            <span className="eyebrow">CURRENT OBJECTIVE</span>
            <h2>What’s next</h2>
            <p>{userData.goals || 'This climber has not published a current objective.'}</p>
            <Link to="/Trips">Explore expeditions</Link>
          </section>
        </aside>
      </div>
    </div>
  );
}

export default UserProfile;
